import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AppShell } from '@/components/templates/AppShell';
import { Aside } from '@/components/templates/Aside';
import { StepNav } from '@/components/organisms/StepNav';
import { StepRail, type StepRailStatus } from '@/components/organisms/StepRail';
import { EditLevelInvalidState, LevelUpExitDialogs, LevelUpTopbar } from './LevelUpFlowPage.parts';
import { dwrClassesById } from '@/data/classes';
import { findMoveByIdAnyScope } from '@/data/moves';
import type { Character } from '@/schemas/character';
import type { Move } from '@/schemas/move';
import { getNextLevel, isLevelUpAvailable } from '@/services/levelUp';
import { loadCharacter } from '@/services/characterStorage';
import { LevelUpFlowSteps } from './levelup/LevelUpFlowSteps';
import { useLevelUpFlowHandlers } from './levelup/useLevelUpFlowHandlers';
import { computeEditModeData, parseEditLevel, validateEditLevel } from './levelup/editModeHelpers';
import { getApplicableSteps, STEP_LABELS, type StepFlowFlags } from './levelup/stepRouting';
import {
  isShellNavStep,
  isStepComplete,
  nextForShellStep,
  prevForShellStep,
} from './levelup/shellNavHelpers';
import { useFlowFlags } from './levelup/useFlowFlags';
import { INITIAL_LEVELUP_DRAFT, type LevelUpDraft, type LevelUpStepId } from './levelup/types';

/**
 * Pagina del LevelUpFlow (D1). PR 8 Fase 2: shell rifatto con `<AppShell>` +
 * `<AppTopbar variant="levelup">` (sfondo ink invertito) + `<MainGrid>` +
 * `<Aside><StepRail>>` + `<StepNav>` esterno (solo per i 3 step rifatti:
 * intro / stat / move). Gli altri 6 step mantengono i loro bottoni interni
 * Indietro/Avanti — scope PR 9-10 per riskin progressivo.
 *
 * **Scope Fase D** (build planner, non progressione in gioco): il flow
 * programma la build del livello successivo del PG. NON consuma PE.
 * Vedi `dev-workspace/docs/Fase-D-Level-Up-SSOT.md` §0.
 *
 * **FI-46 PR-B (edit retroattivo)**: route `/character/edit-level-up/:level`
 * attiva la modalità edit. Il PG viene rewound a fine L{level-1} via
 * `rewindToLevel`, e il draft pre-popolato dal record storico via
 * `levelUpRecordToDraft`. Confermando il flow, i livelli successivi
 * (L{level+1}..L{current}) vengono troncati con `ConfirmDialog` di avviso.
 */
export function LevelUpFlowPage() {
  const [character, setCharacter] = useState<Character | null>(() => loadCharacter());
  const params = useParams<{ level?: string }>();
  const editLevel = parseEditLevel(params.level);

  useEffect(() => {
    setCharacter(loadCharacter());
  }, []);

  if (!character) {
    return <NoCharacterState />;
  }
  if (editLevel !== undefined) {
    // FI-46bis: edit di L1 vive nel builder pre-popolato, non nel flow level-up.
    if (editLevel === 1) return <Navigate to="/character/edit-l1" replace />;
    const validationError = validateEditLevel(editLevel, character);
    if (validationError) return <EditLevelInvalidState reason={validationError} />;
    return (
      <LevelUpFlowInner key={`edit-${editLevel}`} character={character} editLevel={editLevel} />
    );
  }
  if (!isLevelUpAvailable(character)) {
    return <LevelMaxReachedState character={character} />;
  }
  return <LevelUpFlowInner character={character} />;
}

function NoCharacterState() {
  return (
    <main className="mx-auto max-w-3xl p-6">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/character">
          <ArrowLeft aria-hidden />
          Scheda personaggio
        </Link>
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>Nessun personaggio salvato</CardTitle>
          <CardDescription>
            Devi prima creare un personaggio per poterlo far salire di livello.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to="/builder">Vai alla creazione personaggio</Link>
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}

function LevelMaxReachedState({ character }: { character: Character }) {
  return (
    <main className="mx-auto max-w-3xl p-6">
      <Button asChild variant="ghost" size="sm" className="mb-4">
        <Link to="/character">
          <ArrowLeft aria-hidden />
          Scheda personaggio
        </Link>
      </Button>
      <Card>
        <CardHeader>
          <CardTitle>Livello massimo raggiunto</CardTitle>
          <CardDescription>
            Il personaggio ha già raggiunto il livello {character.level}. L&apos;avanzamento oltre
            il 10° livello (Addio / Apprendista / Instancabile — manuale V1 r. 1824) sarà gestito in
            uno step successivo.
          </CardDescription>
        </CardHeader>
      </Card>
    </main>
  );
}

interface LevelUpFlowInnerProps {
  character: Character;
  /**
   * **FI-46 PR-B**: se definito, attiva la modalità edit retroattivo.
   * Il PG mostrato al flow è il PG rewound a fine L{editLevel - 1};
   * il draft è pre-popolato dalle scelte storiche del record L{editLevel}.
   * Confermando, i livelli L{editLevel+1}..L{character.level} vengono
   * troncati (con ConfirmDialog di avviso se >0).
   */
  editLevel?: number;
}

function LevelUpFlowInner({ character: originalCharacter, editLevel }: LevelUpFlowInnerProps) {
  const classDef = dwrClassesById[originalCharacter.classId];

  // **FI-46 PR-B**: in modalità edit, il flow gira su un PG rewound a fine
  // L{editLevel - 1} con draft pre-popolato dal record storico.
  const editData = useMemo(
    () => computeEditModeData(originalCharacter, editLevel),
    [originalCharacter, editLevel],
  );

  const character = editData?.characterForFlow ?? originalCharacter;
  const isEditMode = editData !== null;
  const truncatedLevels = editData?.truncatedLevels ?? 0;

  const [step, setStep] = useState<LevelUpStepId>('intro');
  const [draft, setDraft] = useState<LevelUpDraft>(
    () => editData?.initialDraft ?? INITIAL_LEVELUP_DRAFT,
  );

  const nextLevel = getNextLevel(character);
  const moveDef = useMemo<Move | undefined>(() => {
    if (!draft.moveAdded || !classDef) return undefined;
    return findMoveByIdAnyScope(classDef.id, draft.moveAdded.id);
  }, [draft.moveAdded, classDef]);

  const flags = useFlowFlags(character, draft, nextLevel, moveDef);

  const handlers = useLevelUpFlowHandlers({
    character,
    originalCharacter,
    classDef,
    draft,
    isEditMode,
    editLevel,
    nextLevel,
    truncatedLevels,
  });

  if (!classDef) {
    return (
      <main className="mx-auto max-w-3xl p-6">
        <p className="text-destructive">Classe «{character.classId}» non nel catalogo.</p>
      </main>
    );
  }

  const mainContent = (
    <LevelUpMain
      step={step}
      character={character}
      classDef={classDef}
      draft={draft}
      setDraft={setDraft}
      moveDef={moveDef}
      flags={flags}
      nextLevel={nextLevel}
      onSetStep={setStep}
      onApply={handlers.handleApply}
      applyError={handlers.applyError}
    />
  );

  // FI-53 (Sprint F): LevelUpRail riusato desktop + mobile drawer.
  const railContent = (
    <LevelUpRail
      step={step}
      character={character}
      classDef={classDef}
      draft={draft}
      moveDef={moveDef}
      flags={flags}
      onSetStep={setStep}
    />
  );

  return (
    <>
      <AppShell
        header={
          <LevelUpTopbar
            character={character}
            nextLevel={nextLevel}
            classDefName={classDef.name}
            onCancel={handlers.requestCancel}
            mobileDrawerContent={railContent}
            mobileDrawerWatermarkUrl={`/classes/${character.classId}.png`}
          />
        }
        main={mainContent}
        aside={<Aside watermarkUrl={`/classes/${character.classId}.png`}>{railContent}</Aside>}
      />
      <LevelUpExitDialogs
        cancelOpen={handlers.cancelDialogOpen}
        onCancelOpenChange={handlers.setCancelDialogOpen}
        onCancelConfirm={handlers.confirmCancel}
        isEditMode={isEditMode}
        editLevel={editLevel}
        truncateOpen={handlers.truncateDialogOpen}
        onTruncateOpenChange={handlers.setTruncateDialogOpen}
        onTruncateConfirm={handlers.confirmTruncate}
        truncatedLevels={truncatedLevels}
      />
    </>
  );
}

interface LevelUpMainProps {
  step: LevelUpStepId;
  character: Character;
  classDef: NonNullable<(typeof dwrClassesById)[keyof typeof dwrClassesById]>;
  draft: LevelUpDraft;
  setDraft: (d: LevelUpDraft) => void;
  moveDef: Move | undefined;
  flags: StepFlowFlags;
  nextLevel: number;
  onSetStep: (s: LevelUpStepId) => void;
  onApply: () => void;
  applyError: string | null;
}

function LevelUpMain({
  step,
  character,
  classDef,
  draft,
  setDraft,
  moveDef,
  flags,
  nextLevel,
  onSetStep,
  onApply,
  applyError,
}: LevelUpMainProps) {
  return (
    <>
      {isShellNavStep(step) ? (
        <LevelUpShellNav
          step={step}
          character={character}
          classDef={classDef}
          draft={draft}
          moveDef={moveDef}
          flags={flags}
          nextLevel={nextLevel}
          onSetStep={onSetStep}
          onApply={onApply}
        />
      ) : null}
      <LevelUpFlowSteps
        step={step}
        character={character}
        classDef={classDef}
        nextLevel={nextLevel}
        draft={draft}
        setDraft={setDraft}
        needsGrantedStep={flags.needsGrantedStep}
        needsMulticlassStep={flags.needsMulticlassStep}
        needsArchetypeStep={flags.needsArchetypeStep}
        needsSpellsStep={flags.needsSpellsStep}
        needsPastTenStep={flags.needsPastTenStep}
        moveDef={moveDef}
        applyError={applyError}
      />
    </>
  );
}

// `LevelUpTopbar` estratto in `LevelUpFlowPage.parts.tsx` (FI-53 Sprint F
// iter. 3) per rispettare `max-lines: 400`. Pattern coerente con
// `LevelUpStepSpellsParts.tsx` (PR 10).

// ---------------------------------------------------------------------------
// StepNav esterno — copertura 9 step (PR 10)
// ---------------------------------------------------------------------------

interface ShellNavProps {
  step: LevelUpStepId;
  character: Character;
  classDef: NonNullable<(typeof dwrClassesById)[keyof typeof dwrClassesById]>;
  draft: LevelUpDraft;
  moveDef: Move | undefined;
  flags: StepFlowFlags;
  nextLevel: number;
  onSetStep: (s: LevelUpStepId) => void;
  onApply: () => void;
}

function LevelUpShellNav({
  step,
  character,
  classDef,
  draft,
  moveDef,
  flags,
  nextLevel,
  onSetStep,
  onApply,
}: ShellNavProps) {
  const applicable = getApplicableSteps(flags);
  const currentIndex = Math.max(0, applicable.indexOf(step));
  const total = applicable.length;
  const isComplete = isStepComplete(step, character, classDef, draft, moveDef);
  const isLast = step === 'confirm';
  const onPrev = () => onSetStep(prevForShellStep(step, flags));
  const onNext = () => {
    if (!isComplete) return;
    if (isLast) {
      onApply();
      return;
    }
    onSetStep(nextForShellStep(step, character, draft, flags));
  };
  // **FI-71**: confirm step → titolo grande "Conferma il livello N" già
  // dominante. Suppress il sub-label "CONFERMA" e uniforma bottone a
  // "Finalizza" (pattern builder L1) invece di "Conferma e applica L{N}".
  const currentLabel = isLast ? '' : STEP_LABELS[step];
  const lastLabel = 'Finalizza';
  return (
    <StepNav
      currentIndex={currentIndex}
      total={total}
      currentLabel={currentLabel}
      centerSubLabel={`Step ${currentIndex + 1} / ${total} · Sali a L${nextLevel}`}
      isCurrentComplete={isComplete}
      isLast={isLast}
      lastLabel={lastLabel}
      onPrev={onPrev}
      onNext={onNext}
      disableNextWhenIncomplete
    />
  );
}

// ---------------------------------------------------------------------------
// StepRail aside
// ---------------------------------------------------------------------------

function LevelUpRail({
  step,
  character,
  classDef,
  draft,
  moveDef,
  flags,
  onSetStep,
}: {
  step: LevelUpStepId;
  character: Character;
  classDef: NonNullable<(typeof dwrClassesById)[keyof typeof dwrClassesById]>;
  draft: LevelUpDraft;
  moveDef: Move | undefined;
  flags: StepFlowFlags;
  onSetStep: (s: LevelUpStepId) => void;
}) {
  const applicable = getApplicableSteps(flags);
  const sections = applicable.map((id, i) => ({
    id,
    label: STEP_LABELS[id],
    stepNumber: String(i + 1).padStart(2, '0'),
  }));
  return (
    <StepRail
      sections={sections}
      currentId={step}
      getStatus={(id) => stepStatusFor(id as LevelUpStepId, character, classDef, draft, moveDef)}
      onJump={(id) => onSetStep(id as LevelUpStepId)}
    />
  );
}

/**
 * **FI-89 (2026-05-24)** — SSOT delegate a `shellNavHelpers::isStepComplete`.
 * Precedente predicato povero `STEP_DONE_PREDICATES` (presence-based su
 * `draft.*`) ignorava la validità semantica del setup: caso pilota lo step
 * `spells` con grant auto-only (`count: undefined`) restava "todo" pur
 * essendo legittimamente completo. `confirm` resta sempre "todo" (diventa
 * done solo dopo l'apply, momento in cui il flow esce — semantica sidepanel).
 */
function stepStatusFor(
  id: LevelUpStepId,
  character: Character,
  classDef: NonNullable<(typeof dwrClassesById)[keyof typeof dwrClassesById]>,
  draft: LevelUpDraft,
  moveDef: Move | undefined,
): StepRailStatus {
  if (id === 'confirm') return 'todo';
  return isStepComplete(id, character, classDef, draft, moveDef) ? 'done' : 'todo';
}
